import { ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { buildClientTokenContext } from "@/contexts/clientAccessTokenContext";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { useClientByTokenQuery } from "@/graphql/queries/clientByToken.graphql.types";
import { usePackageSuggestionsQuery } from "@/graphql/queries/packageSuggestions.graphql.types";
import { MedspaOnlineStoreData } from "@/views/online-store/onlineStoreView";

type UseGetPackageSuggestionsProps = {
  clientAccessToken: string;
  clientId: string;
  packagesInfo: MedspaOnlineStoreData["packages"];
  packagesSuggestionsV1Enabled: boolean;
  serviceMenuItemLineIds?: string[];
  error?: string | ApolloError;
  loading?: boolean;
};

export function useGetPackageSuggestions({
  clientAccessToken,
  clientId,
  packagesInfo,
  packagesSuggestionsV1Enabled,
  serviceMenuItemLineIds,
  error,
  loading,
}: UseGetPackageSuggestionsProps) {
  const { data: packageSuggestionsData, loading: packageSuggestionsLoading } =
    usePackageSuggestionsQuery({
      variables: {
        clientId: clientId,
        serviceMenuItemLineIds,
      },
      context: buildClientTokenContext(clientAccessToken),
      skip:
        !clientId ||
        serviceMenuItemLineIds.length === 0 ||
        !packagesSuggestionsV1Enabled,
    });

  const suggestedPackageIds = useMemo(
    () =>
      packageSuggestionsData?.medspaPackageSuggestions.suggestedPackages.map(
        (packageItem) => packageItem.id
      ) ?? [],
    [packageSuggestionsData]
  );

  const suggestedPackages = useMemo(
    () =>
      packagesInfo?.filter((packageItem) =>
        suggestedPackageIds.includes(packageItem.id)
      ) ?? [],
    [packagesInfo, suggestedPackageIds]
  );

  return useMemo(
    () => ({
      suggestedPackages,
      loading:
        !error &&
        (loading ||
          packageSuggestionsLoading ||
          packageSuggestionsData === undefined),
      error,
    }),
    [
      suggestedPackages,
      error,
      loading,
      packageSuggestionsLoading,
      packageSuggestionsData,
    ]
  );
}

export function useGetPackageSuggestionsBooking() {
  const {
    visitId,
    clientAccessToken,
    packagesInfo,
    packagesSuggestionsV1Enabled,
  } = useClientBookingContext();

  const {
    data,
    error: queryError,
    loading,
  } = useClientByTokenQuery({
    variables: {
      token: clientAccessToken as string,
    },
    context: buildClientTokenContext(clientAccessToken),
    skip: !clientAccessToken || !packagesSuggestionsV1Enabled,
  });

  const error =
    data?.clientAccessTokenByPk === null
      ? "Client Access Token expired"
      : queryError;

  const { visits, id: clientId } = data?.clientAccessTokenByPk?.client ?? {};

  const serviceMenuItemLineIds =
    visits
      ?.find((visit) => visit.id == visitId)
      ?.serviceMenuItemLines?.map(
        (serviceMenuItemLine) => serviceMenuItemLine.id
      ) ?? [];

  return useGetPackageSuggestions({
    clientId,
    clientAccessToken,
    packagesInfo,
    packagesSuggestionsV1Enabled,
    serviceMenuItemLineIds,
    error,
    loading,
  });
}
